import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Alert,
  IconButton,
  Skeleton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import axios from 'axios';
import TechnicalWorkspaceTabs from './TechnicalWorkspaceTabs';
import PeriodPicker, { usePersistedPeriod } from './PeriodPicker';
import AssetPicker from './AssetPicker';
import ConstraintParetoCard from './ConstraintParetoCard';
import { useThemeColors } from '../theme/ThemeModeContext';
// Shared picker→params helpers — INT digits derive from the ISO instant's
// UTC fields so this page queries the exact same window as every ISO-based
// endpoint (the old local-getter isoToInt skewed it by the viewer's UTC
// offset; see utils/periodParams.js).
import { periodApiParams, periodNemInts } from '../utils/periodParams';

import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ScatterPlot } from '@mui/x-charts/ScatterChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip';
import { useItemTooltip } from '@mui/x-charts/ChartsTooltip';
import { useXAxis, useYAxis } from '@mui/x-charts/hooks';

import {
  ChartSection,
  formatDateTime,
  formatMWh,
  CHART_MARGINS,
  rotatedTickLabelStyle,
  tickLabelStyle,
} from './charts';
import { ENERGY_PALETTE } from './WaterfallChart';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// ── Colours (shared across all assets) ─────────────────────────────────────
// Canonical curtailment-cause palette (ENERGY_PALETTE) — the same hues used on
// the Technical / Commercial / Overview pages, so each concept reads in one
// colour everywhere: generation = green, economic (neg-price) = red,
// grid (network) = blue, potential = slate.
const CURTAILMENT_COLORS = {
  actual: ENERGY_PALETTE.delivered,    // green — energy actually generated
  economic: ENERGY_PALETTE.econ_curt,  // red — generator backed off (price ≤ 0)
  grid: ENERGY_PALETTE.grid_curt,      // blue — network / system constraint
  potential: ENERGY_PALETTE.potential, // slate — max possible (dashed target line)
};

// ── Helpers ────────────────────────────────────────────────────────────────

// PeriodPicker emits ISO start/end + a frequency token; the curtailment
// endpoint wants an INT YYYYMMDDHHMM window and one of 30MIN/DAILY/MONTHLY/
// YEARLY. These map between the two.
const CURT_FREQUENCIES = ['30min', 'daily', 'monthly', 'yearly'];
// PeriodPicker presets can suggest finer/odd frequencies (5min / hourly /
// weekly) — snap each to the nearest frequency the chart + backend can bucket.
const CURT_FREQ_NORMALISE = {
  '5min': '30min', '30min': '30min', 'hourly': 'daily',
  'daily': 'daily', 'weekly': 'daily',
  'monthly': 'monthly', 'yearly': 'yearly',
};
const CURT_FREQ_API = { '30min': '30MIN', 'daily': 'DAILY', 'monthly': 'MONTHLY', 'yearly': 'YEARLY' };

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Small dashed-line glyph for the legend + tooltip. Matches the per-asset
// potential overlay below and the Facility & Covenants forecast ticks — a
// short dashed segment reads as a discrete target marker, not a swept line.
const DashGlyph = ({ width = 18, stroke = CURTAILMENT_COLORS.potential }) => (
  <Box component="svg" viewBox="0 0 18 6" sx={{ width, height: 6, flexShrink: 0 }}>
    <line
      x1={1} y1={3} x2={17} y2={3}
      stroke={stroke} strokeWidth={3} strokeDasharray="2 3" strokeLinecap="round"
    />
  </Box>
);

/**
 * Custom SVG overlay rendered inside ChartContainer.
 *
 * Why custom: MUI x-charts line+mark series only support one x position per
 * bucket. With N assets grouped side-by-side per bucket, we need N distinct
 * x positions per bucket (one potential tick per asset's stack). This reads
 * the chart's x/y scales directly via hooks and draws a short dashed
 * horizontal segment — the Facility & Covenants forecast-tick style — over
 * each asset's stack at its potential y value, so each tick reads as that
 * asset's discrete "max possible" target.
 *
 * `buckets` is the sorted bucket key array (matching the x-axis data).
 * `assetIds` is sorted to match the bar series order within each band.
 * `lookup` is keyed by `${bucket}|${assetId}` → potential_mwh.
 */
const PerAssetPotentialOverlay = ({ xLabels, buckets, assetIds, lookup }) => {
  const xAxis = useXAxis('band');
  const yAxis = useYAxis('y');
  if (!xAxis || !yAxis) return null;

  const xScale = xAxis.scale;          // band scale: label → px (left edge of band)
  const yScale = yAxis.scale;          // linear scale: mwh → px
  const bandwidth = xScale.bandwidth ? xScale.bandwidth() : 0;
  // MUI lays out N independent stacks evenly inside each band with a default
  // padding on both sides. Reserve ~8% inset to match.
  const bandInset = bandwidth * 0.08;
  const usable = bandwidth - bandInset * 2;
  const subWidth = assetIds.length > 0 ? usable / assetIds.length : 0;
  const dashHalf = subWidth * 0.4;     // ~80% of the asset's sub-band, like ForecastTicks

  const marks = [];
  // xLabels[i] is the string that matches the band scale domain; buckets[i]
  // is the raw date_time key used by the data lookup. We iterate in lockstep.
  xLabels.forEach((label, bucketIdx) => {
    const bandLeft = xScale(label);
    if (bandLeft == null || Number.isNaN(bandLeft)) return;
    const bucketKey = buckets[bucketIdx];
    assetIds.forEach((aid, i) => {
      const value = lookup[`${bucketKey}|${aid}`];
      if (value == null) return;
      const cx = bandLeft + bandInset + subWidth * (i + 0.5);
      const cy = yScale(value);
      if (Number.isNaN(cy)) return;
      marks.push(
        <g key={`${bucketKey}-${aid}`} style={{ pointerEvents: 'none' }}>
          {/* white halo so the slate dashes stay legible over the coloured bars */}
          <line
            x1={cx - dashHalf} x2={cx + dashHalf} y1={cy} y2={cy}
            stroke="#ffffff" strokeWidth={6} strokeDasharray="2 3" strokeLinecap="round"
          />
          <line
            x1={cx - dashHalf} x2={cx + dashHalf} y1={cy} y2={cy}
            stroke={CURTAILMENT_COLORS.potential} strokeWidth={3}
            strokeDasharray="2 3" strokeLinecap="round"
          />
        </g>,
      );
    });
  });
  return <g className="curtailment-potential-overlay">{marks}</g>;
};

// ── Custom per-asset tooltip content ───────────────────────────────────────
//
// When the user hovers any bar OR x mark, show a single compact block with
// ALL four values for that (asset, bucket) pair: actual, economic curtailment,
// grid curtailment, and potential. This avoids the MUI default "axis mode"
// dump that shows every series in the bucket (12+ lines for a 4-asset
// portfolio), and avoids the "item mode" single-line which loses context.
//
// The hovered series id follows the pattern `<type>-<aid>` (see bar series
// creation below), so we can extract the asset id and use it plus the
// `dataIndex` to index back into the caller's row lookup.
const PerAssetTooltipContent = ({ bucketsArr, assetNames, rowLookup, frequency }) => {
  const tooltip = useItemTooltip();
  if (!tooltip) return null;

  const { identifier } = tooltip;
  const seriesId = String(identifier?.seriesId || '');
  // series id format: "actual-<aid>" | "econ-<aid>" | "grid-<aid>"
  const dashIdx = seriesId.indexOf('-');
  const aid = dashIdx >= 0 ? seriesId.slice(dashIdx + 1) : null;
  const dataIndex = identifier?.dataIndex;
  if (aid == null || dataIndex == null) return null;

  const bucketKey = bucketsArr[dataIndex];
  const row = rowLookup[`${bucketKey}|${aid}`];
  if (!row) return null;

  const name = assetNames[aid] || aid;
  const potential = row.potential_mwh || 0;
  const actual = row.actual_mwh || 0;
  const econ = row.econ_curtailed_mwh || 0;
  const grid = row.grid_curtailed_mwh ?? row.network_curtailed_mwh ?? 0;
  const totalCurt = econ + grid;
  const curtPct = potential > 0 ? (totalCurt / potential) * 100 : 0;

  const Row = ({ colour, label, value, glyph }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, lineHeight: 1.6 }}>
      {glyph === 'dash' ? (
        <DashGlyph width={14} stroke={colour} />
      ) : (
        <Box sx={{ width: 10, height: 10, bgcolor: colour, borderRadius: '2px', flexShrink: 0 }} />
      )}
      <Box sx={{ color: 'text.secondary', flexGrow: 1 }}>{label}</Box>
      <Box sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>
        {formatMWh(value)}
      </Box>
    </Box>
  );

  return (
    <Paper
      sx={{
        p: 1.5,
        minWidth: 240,
        border: '1px solid rgba(255, 255, 255, 0.10)',
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
        borderRadius: 1.5,
      }}
    >
      <Box sx={{ fontWeight: 800, fontSize: 13, lineHeight: 1.35, mb: 0.25 }}>
        {name}
      </Box>
      <Box
        sx={{
          fontSize: 11,
          color: 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
          mb: 1,
        }}
      >
        {/* Same formatter as the x-axis ticks — not the raw INT bucket key. */}
        {formatDateTime(bucketKey, frequency)}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Row colour={CURTAILMENT_COLORS.potential} label="Potential" value={potential} glyph="dash" />
        <Row colour={CURTAILMENT_COLORS.actual} label="Actual" value={actual} />
        <Row colour={CURTAILMENT_COLORS.economic} label="Economic curtailment" value={econ} />
        <Row colour={CURTAILMENT_COLORS.grid} label="Grid curtailment" value={grid} />
      </Box>
      <Box
        sx={{
          mt: 1,
          pt: 1,
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 11,
        }}
      >
        <Box sx={{ color: 'text.secondary' }}>Total curtailed</Box>
        <Box sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
          {curtPct.toFixed(1)}% ({formatMWh(totalCurt)})
        </Box>
      </Box>
    </Paper>
  );
};

// ── Chart: grouped stacked bars per asset per bucket + per-asset X markers ──

// Small inline legend rendered above the chart. We draw it ourselves because
// MUI's ChartsLegend would generate 4×N items (one per bar series) which is
// unreadable. Here we show 4 shared-colour entries plus a one-line per-asset
// key under the chart so users can map bar positions → asset names.
const ChartLegend = ({ assetIds, assetNames }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 3,
      rowGap: 1,
      mb: 2,
      fontSize: '0.78rem',
    }}
  >
    {[
      { label: 'Actual Generation', color: CURTAILMENT_COLORS.actual, shape: 'square' },
      { label: 'Economic Curtailment', color: CURTAILMENT_COLORS.economic, shape: 'square' },
      { label: 'Grid Curtailment', color: CURTAILMENT_COLORS.grid, shape: 'square' },
      { label: 'Potential (max possible)', color: CURTAILMENT_COLORS.potential, shape: 'dash' },
    ].map((item) => (
      <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {item.shape === 'square' ? (
          <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: item.color }} />
        ) : (
          <DashGlyph stroke={item.color} />
        )}
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{item.label}</Typography>
      </Box>
    ))}
    {assetIds.length > 1 && (
      <Typography
        sx={{
          fontSize: '0.72rem',
          color: 'text.secondary',
          fontStyle: 'italic',
          ml: 'auto',
          maxWidth: '45%',
          textAlign: 'right',
        }}
      >
        Within each bucket, bars are ordered left→right:{' '}
        {assetIds.map((aid, i) => (
          <React.Fragment key={aid}>
            <strong>{assetNames[aid] || aid}</strong>
            {i < assetIds.length - 1 ? ', ' : ''}
          </React.Fragment>
        ))}
      </Typography>
    )}
  </Box>
);

const CurtailmentGroupedChart = ({ curtailment, assets, frequency }) => {
  const {
    buckets, assetIds, assetNames, seriesData, potentialLookup, rowLookup,
  } = useMemo(() => {
    if (!curtailment || curtailment.length === 0) {
      return {
        buckets: [], assetIds: [], assetNames: {}, seriesData: null,
        potentialLookup: {}, rowLookup: {},
      };
    }

    // Build ordered buckets and asset ids
    const bucketSet = new Set();
    const assetSet = new Set();
    curtailment.forEach((r) => {
      bucketSet.add(String(r.date_time));
      assetSet.add(r.asset_id);
    });
    const bucketsArr = Array.from(bucketSet).sort();
    const assetsArr = Array.from(assetSet).sort();

    // Row lookup (bucket,asset) → full row (used by tooltip + overlay)
    const rowLk = {};
    curtailment.forEach((r) => {
      rowLk[`${r.date_time}|${r.asset_id}`] = r;
    });

    // Asset metadata
    const nameMap = {};
    (assets || []).forEach((a) => {
      nameMap[a.asset_id] = a.name || a.asset_id;
    });

    // Per-asset bar series data (actual / econ / grid)
    const data = { actual: {}, econ: {}, grid: {} };
    assetsArr.forEach((aid) => {
      data.actual[aid] = bucketsArr.map((b) => rowLk[`${b}|${aid}`]?.actual_mwh ?? 0);
      data.econ[aid] = bucketsArr.map((b) => rowLk[`${b}|${aid}`]?.econ_curtailed_mwh ?? 0);
      data.grid[aid] = bucketsArr.map((b) => rowLk[`${b}|${aid}`]?.grid_curtailed_mwh ?? 0);
    });

    // Lookup for X overlay: (bucket|asset_id) → potential_mwh
    const potLookup = {};
    curtailment.forEach((r) => {
      potLookup[`${r.date_time}|${r.asset_id}`] = r.potential_mwh;
    });

    return {
      buckets: bucketsArr,
      assetIds: assetsArr,
      assetNames: nameMap,
      seriesData: data,
      potentialLookup: potLookup,
      rowLookup: rowLk,
    };
  }, [curtailment, assets]);

  if (!curtailment || curtailment.length === 0 || !seriesData) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No curtailment data available — requires intermittent generators (Solar / Wind) from Aug 2024+.
        </Typography>
      </Box>
    );
  }

  const xLabels = buckets.map((b) => formatDateTime(b, frequency));

  // Build 3 × N bar series (actual + econ + grid per asset). Each asset has
  // its own stack id so its three bars stack vertically; distinct stacks are
  // side-by-sided by MUI within each band. Every series gets a per-asset label
  // so tooltips identify the asset correctly.
  const series = [];
  assetIds.forEach((aid) => {
    const displayName = assetNames[aid] || aid;
    series.push({
      type: 'bar',
      id: `actual-${aid}`,
      label: `${displayName} — Actual`,
      data: seriesData.actual[aid],
      stack: `stk-${aid}`,
      color: CURTAILMENT_COLORS.actual,
      valueFormatter: (v) => formatMWh(v || 0),
    });
    series.push({
      type: 'bar',
      id: `econ-${aid}`,
      label: `${displayName} — Economic`,
      data: seriesData.econ[aid],
      stack: `stk-${aid}`,
      color: CURTAILMENT_COLORS.economic,
      valueFormatter: (v) => formatMWh(v || 0),
    });
    series.push({
      type: 'bar',
      id: `grid-${aid}`,
      label: `${displayName} — Grid`,
      data: seriesData.grid[aid],
      stack: `stk-${aid}`,
      color: CURTAILMENT_COLORS.grid,
      valueFormatter: (v) => formatMWh(v || 0),
    });
  });

  // Height scales with number of assets so the grouped bars stay legible.
  const chartHeight = Math.max(460, 400 + assetIds.length * 10);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <ChartLegend assetIds={assetIds} assetNames={assetNames} />
      {/*
        Let ChartContainer auto-size to its parent via ResizeObserver. Omitting
        `width`/`height` and using a parent Box with explicit sizing is the
        idiomatic MUI x-charts responsive pattern and avoids the stale-width
        issue we hit with the previous useContainerWidth approach.
      */}
      <Box sx={{ width: '100%', height: chartHeight }}>
        <ChartContainer
          series={series}
          hideLegend
          xAxis={[{
            id: 'band',
            scaleType: 'band',
            data: xLabels,
            // Name the axis after the picker's bucketing so the reader knows
            // what one bar spans without decoding the tick format.
            label: { '30MIN': '30-minute interval', 'DAILY': 'Day', 'MONTHLY': 'Month', 'YEARLY': 'Year' }[frequency] || 'Period',
            labelStyle: { fontSize: 11 },
            // MUI X v8: each axis reserves its OWN height (default 28px) —
            // the chart margin no longer covers it. The −45° date ticks +
            // the axis label need ~90px or they clip to nothing ("the chart
            // has no x axis").
            height: 90,
            tickLabelStyle: rotatedTickLabelStyle,
            categoryGapRatio: 0.15,
            barGapRatio: 0.05,
          }]}
          yAxis={[{ id: 'y', valueFormatter: formatMWh, tickLabelStyle }]}
          margin={{ ...CHART_MARGINS, bottom: 16 }}
        >
          <ChartsGrid horizontal />
          <BarPlot borderRadius={2} />
          <PerAssetPotentialOverlay
            xLabels={xLabels}
            buckets={buckets}
            assetIds={assetIds}
            lookup={potentialLookup}
          />
          <ChartsXAxis axisId="band" />
          <ChartsYAxis axisId="y" />
          {/*
            Custom per-asset tooltip. ChartsTooltipContainer in item mode fires
            on the single bar that the pointer is over; our content component
            then looks up the FULL per-asset row (actual + econ + grid +
            potential) and renders a compact 4-line block — not 12 lines for
            all assets in the bucket.
          */}
          <ChartsTooltipContainer trigger="item">
            <PerAssetTooltipContent
              bucketsArr={buckets}
              assetNames={assetNames}
              rowLookup={rowLookup}
              frequency={frequency}
            />
          </ChartsTooltipContainer>
        </ChartContainer>
      </Box>
    </Box>
  );
};

// ── Curtailment vs Spot Price scatter ─────────────────────────────────────
//
// Mirrors the Technical Performance "Curtailment vs spot price" card: price on
// X, curtailed MWh on Y, one dot per (asset, bucket, cause). Each cause is
// plotted at the price during ITS OWN lost intervals — economic (red) at
// econ_rrp (≤0 by construction) and grid (blue) at grid_rrp (>0) — so economic
// curtailment can never appear at a positive price.

const fmtPriceAxis = (v) => `A$${Math.round(v)}`;

// Tooltip — cause-coloured header + asset / date / spot price / curtailed MWh.
const CurtailmentVsPriceTooltip = ({ pointLookup }) => {
  const tooltip = useItemTooltip();
  if (!tooltip) return null;
  const { identifier } = tooltip;
  // identifier.seriesId: "econ" | "grid", dataIndex: index within that series.
  const seriesId = String(identifier?.seriesId || '');
  const dataIndex = identifier?.dataIndex;
  if (!seriesId || dataIndex == null) return null;

  const point = pointLookup[seriesId]?.[dataIndex];
  if (!point) return null;

  const Row = ({ label, value, valueColor }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        fontSize: 12,
        lineHeight: 1.7,
      }}
    >
      <Box sx={{ color: 'text.secondary' }}>{label}</Box>
      <Box
        sx={{
          fontFamily: 'monospace',
          fontWeight: 600,
          color: valueColor || 'text.primary',
        }}
      >
        {value}
      </Box>
    </Box>
  );

  return (
    <Paper
      sx={{
        p: 1.5,
        minWidth: 220,
        border: '1px solid rgba(255, 255, 255, 0.10)',
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
        borderRadius: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 800,
          fontSize: 13,
          lineHeight: 1.35,
          mb: 0.75,
          color: point.color,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: point.color,
            flexShrink: 0,
          }}
        />
        {point.causeLabel}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Row label="Asset" value={point.assetName} />
        <Row label="Date" value={point.bucketLabel} />
        <Row label="Spot price" value={point.price == null ? 'n/a' : `A$${point.price.toFixed(2)}/MWh`} />
        <Row label="Curtailed" value={formatMWh(point.y)} valueColor={point.color} />
      </Box>
    </Paper>
  );
};

const CurtailmentVsPriceScatter = ({ curtailment, frequency, assets }) => {
  const { series, pointLookup, hasPoints } = useMemo(() => {
    const nameMap = {};
    (assets || []).forEach((a) => { nameMap[a.asset_id] = a.name || a.asset_id; });

    // Two point sets — each cause plotted at the price during its OWN lost
    // intervals: economic at econ_rrp (≤0), grid at grid_rrp (>0).
    const econ = [];
    const grid = [];
    (curtailment || []).forEach((r) => {
      const k = String(r.date_time);
      const meta = {
        bucketLabel: formatDateTime(k, frequency),
        assetName: nameMap[r.asset_id] || r.asset_id,
      };
      const e = r.econ_curtailed_mwh || 0;
      const g = r.grid_curtailed_mwh || 0;
      if (e > 0 && r.econ_rrp != null) {
        econ.push({
          x: r.econ_rrp, y: e, id: `e-${r.asset_id}-${k}`, price: r.econ_rrp,
          color: CURTAILMENT_COLORS.economic, causeLabel: 'Economic curtailment', ...meta,
        });
      }
      if (g > 0 && r.grid_rrp != null) {
        grid.push({
          x: r.grid_rrp, y: g, id: `g-${r.asset_id}-${k}`, price: r.grid_rrp,
          color: CURTAILMENT_COLORS.grid, causeLabel: 'Grid curtailment', ...meta,
        });
      }
    });

    // Drop empty series — MUI x-charts' ScatterPlot throws on a 0-length data
    // array (Flatbush "Unexpected numItems value: 0").
    const s = [];
    if (econ.length) s.push({ type: 'scatter', id: 'econ', label: 'Economic curtailment', data: econ, color: CURTAILMENT_COLORS.economic, markerSize: 5 });
    if (grid.length) s.push({ type: 'scatter', id: 'grid', label: 'Grid curtailment', data: grid, color: CURTAILMENT_COLORS.grid, markerSize: 5 });

    return { series: s, pointLookup: { econ, grid }, hasPoints: econ.length + grid.length > 0 };
  }, [curtailment, frequency, assets]);

  if (!hasPoints) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No curtailment in this window.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', mb: 1.5 }}>
        {series.map((s) => (
          <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: s.color }} />
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* 560 px since the METIS review — at the old size the point cloud
          bunched into one corner and the causes couldn't be told apart. */}
      <Box sx={{ width: '100%', height: 560 }}>
        <ChartContainer
          hideLegend
          margin={{ ...CHART_MARGINS, bottom: 56, left: 80 }}
          series={series}
          xAxis={[{
            id: 'x',
            scaleType: 'linear',
            valueFormatter: fmtPriceAxis,
            tickLabelStyle,
            label: 'Spot price (A$/MWh)',
          }]}
          yAxis={[{
            id: 'y',
            scaleType: 'linear',
            label: 'Curtailed (MWh)',
            valueFormatter: formatMWh,
            tickLabelStyle,
            min: 0,
          }]}
        >
          <ChartsGrid horizontal vertical />
          <ScatterPlot />
          <ChartsXAxis axisId="x" />
          <ChartsYAxis axisId="y" />
          <ChartsTooltipContainer trigger="item">
            <CurtailmentVsPriceTooltip pointLookup={pointLookup} />
          </ChartsTooltipContainer>
        </ChartContainer>
      </Box>
    </Box>
  );
};

// ── Loading skeleton ────────────────────────────────────────────────────────
//
// Reserves the same vertical footprint as the loaded view (waterfall +
// chart sections) so the page doesn't visibly jump/reflow when data lands.
const CurtailmentSkeleton = () => (
  <Box>
    <Skeleton variant="rounded" height={560} sx={{ mb: 3, borderRadius: 1 }} />
    <Skeleton variant="rounded" height={520} sx={{ borderRadius: 1 }} />
  </Box>
);

// ── Grid-curtailment cause diagnostic ──────────────────────────────────────
// The binding network constraints behind grid curtailment: energy lost to each,
// how many intervals, and how hard each pushed the asset's local price below the
// regional price (severity, A$/MWh). Doesn't change the econ/grid split — it
// explains the grid portion.
const GridConstraintsCard = ({ constraints, gridConfirmation, loading, assets }) => {
  const nameMap = useMemo(() => {
    const m = {};
    (assets || []).forEach((a) => { m[a.asset_id] = a.name || a.asset_id; });
    return m;
  }, [assets]);

  if (loading) {
    return <Skeleton variant="rounded" height={220} sx={{ borderRadius: 1 }} />;
  }

  const hasConstraints = constraints && constraints.length > 0;
  // Fresh (T+1) grid confirmation — present even when the named constraint
  // table is empty (a recent window the monthly SPD library hasn't reached).
  // When neither has anything, nothing drove grid curtailment.
  const gc = gridConfirmation && gridConfirmation.confirmed_mwh > 0
    ? gridConfirmation : null;

  if (!hasConstraints && !gc) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No grid curtailment attributable to network constraints in this window.
        </Typography>
      </Box>
    );
  }

  const Th = ({ children, align }) => (
    <Box component="th" sx={{
      py: 1, px: 1.5, textAlign: align || 'left', fontWeight: 700,
      fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase',
      color: 'text.secondary', whiteSpace: 'nowrap',
    }}>{children}</Box>
  );
  const Td = ({ children, align, sx }) => (
    <Box component="td" sx={{
      py: 1, px: 1.5, textAlign: align || 'left', verticalAlign: 'top',
      fontSize: '0.82rem', ...sx,
    }}>{children}</Box>
  );

  const freshDate = gc && gc.fresh_to
    ? new Date(gc.fresh_to).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
    : null;
  const sevAud = gc && gc.median_severity_aud != null
    ? Math.abs(Math.round(gc.median_severity_aud)).toLocaleString('en-AU')
    : null;

  return (
    <Box>
      {/* Fresh (T+1) grid-curtailment confirmation: SEMIDISPATCHCAP (AEMO
          capped the unit) as the headline + local-price depression as the
          severity layer. Covers recent windows the per-constraint table
          (frozen to the monthly SPD library) can't reach. */}
      {gc && (
        <Box sx={{
          mb: hasConstraints ? 2.5 : 0,
          p: 1.75, borderRadius: 1,
          borderLeft: '3px solid', borderColor: CURTAILMENT_COLORS.grid,
          bgcolor: 'action.hover',
        }}>
          <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
            <Typography sx={{
              fontFamily: 'monospace', fontWeight: 700, fontSize: '1.05rem',
              color: CURTAILMENT_COLORS.grid,
            }}>
              {formatMWh(gc.confirmed_mwh)}
            </Typography>
            <Typography sx={{ fontSize: '0.86rem' }}>
              ({gc.coverage_pct}% of grid curtailment) confirmed constrained off — semi-dispatch cap
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
            {sevAud && gc.severity_mwh > 0
              ? `Local price typically A$${sevAud}/MWh below the regional price on ${formatMWh(gc.severity_mwh)} · `
              : ''}
            {gc.intervals} intervals · AEMO dispatch{freshDate ? `, live to ${freshDate}` : ''}
          </Typography>
        </Box>
      )}
      {hasConstraints ? (
        <>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <Th>Asset</Th>
              <Th>Constraint</Th>
              <Th>What it limits</Th>
              <Th align="right">Grid lost</Th>
              <Th align="right">Intervals</Th>
              <Th align="right">Severity</Th>
            </Box>
          </Box>
          <Box component="tbody">
            {constraints.map((c, i) => (
              <Box
                component="tr"
                key={`${c.asset_id}-${c.constraint_id}-${i}`}
                sx={{ borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.05)' }}
              >
                <Td sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{nameMap[c.asset_id] || c.asset_id}</Td>
                <Td sx={{ fontFamily: 'monospace', fontSize: '0.76rem', color: CURTAILMENT_COLORS.grid, whiteSpace: 'nowrap' }}>
                  {c.constraint_id}
                </Td>
                <Td sx={{ color: 'text.secondary', minWidth: 260, maxWidth: 460 }}>{c.description || '—'}</Td>
                <Td align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatMWh(c.grid_mwh)}</Td>
                <Td align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{c.intervals}</Td>
                <Td align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {`A$${Math.round(c.avg_severity_aud).toLocaleString('en-AU')}`}
                </Td>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.disabled', fontStyle: 'italic' }}>
        Severity = how far (A$/MWh) the binding constraint pushed the asset&apos;s local price below the
        regional reference price during the curtailed intervals. Higher = more binding.
      </Typography>
        </>
      ) : (
        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.disabled', fontStyle: 'italic' }}>
          Per-constraint breakdown isn&apos;t available for this window yet — AEMO publishes the
          constraint-factor library (SPDCONNECTIONPOINTCONSTRAINT) ~6 weeks after dispatch. The
          figure above is from AEMO&apos;s fresh next-day local pricing.
        </Typography>
      )}
    </Box>
  );
};


// ── Main page component ───────────────────────────────────────────────────

const PortfolioCurtailment = () => {
  const { portfolioId } = useParams();
  const colors = useThemeColors();

  // Period selector — shared across every Technical tab via sessionStorage,
  // with the picker's suggested frequency snapped to one the curtailment
  // backend can bucket on the way in AND on first read (the persisted value
  // may carry a frequency another tab chose).
  const [rawPeriod, setPersistedPeriod] = usePersistedPeriod('lumeros.technical.period', '30d');
  const period = useMemo(
    () => ({ ...rawPeriod, frequency: CURT_FREQ_NORMALISE[rawPeriod.frequency] || 'daily' }),
    [rawPeriod],
  );

  const [data, setData] = useState(null);
  // Starts true — the page auto-loads on mount, so the first paint shows the
  // skeleton (reserving the cards + chart heights) instead of jumping when
  // data arrives.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handlePeriodChange = useCallback((p) => {
    setPersistedPeriod({ ...p, frequency: CURT_FREQ_NORMALISE[p.frequency] || p.frequency });
  }, [setPersistedPeriod]);

  const load = useCallback(async (p) => {
    if (!p?.start || !p?.end) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/curtailment`,
        {
          params: {
            ...periodNemInts(p),
            frequency: CURT_FREQ_API[p.frequency] || 'DAILY',
          },
        },
      );
      setData(res.data);
    } catch (err) {
      console.error('Curtailment fetch error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load curtailment data');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  // Auto-load on mount and whenever the period changes.
  useEffect(() => { load(period); }, [load, period]);

  // Diagnostic overlay — binding network constraints behind grid curtailment.
  // Loads independently so the main page isn't blocked by the heavier query.
  const [constraints, setConstraints] = useState([]);
  // Fresh (T+1) per-asset grid confirmation, same endpoint. SEMIDISPATCHCAP
  // (AEMO capped the unit) is the headline; local-price depression adds
  // severity — covers recent windows the per-constraint table (frozen to the
  // monthly SPD library) can't reach.
  const [gridConfirmation, setGridConfirmation] = useState([]);
  const [constraintsLoading, setConstraintsLoading] = useState(false);
  useEffect(() => {
    if (!period?.start || !period?.end) return undefined;
    let cancelled = false;
    setConstraintsLoading(true);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/curtailment/constraints`, {
      params: {
        ...periodNemInts(period),
        frequency: CURT_FREQ_API[period.frequency] || 'DAILY',
      },
    })
      .then((r) => {
        if (cancelled) return;
        setConstraints(r.data?.constraints || []);
        setGridConfirmation(r.data?.grid_confirmation || []);
      })
      .catch(() => { if (!cancelled) { setConstraints([]); setGridConfirmation([]); } })
      .finally(() => { if (!cancelled) setConstraintsLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, period]);

  const intermittentAssets = useMemo(
    () => data?.intermittent_assets || [],
    [data],
  );
  const hasIntermittent = intermittentAssets.length > 0;

  // Asset scope — the whole page reads for ONE asset, picked from the header
  // (same chrome as Technical Performance). The picker only renders when the
  // portfolio has more than one intermittent asset; /curtailment already
  // returns every asset's rows, so switching is a client-side filter, not a
  // refetch.
  const [assetChoice, setAssetChoice] = useState(null);
  const assetId = assetChoice || intermittentAssets[0]?.asset_id || null;
  const scopedAssets = useMemo(
    () => intermittentAssets.filter((a) => a.asset_id === assetId),
    [intermittentAssets, assetId],
  );
  const scopedCurtailment = useMemo(
    () => (data?.curtailment || []).filter((r) => r.asset_id === assetId),
    [data, assetId],
  );
  const scopedConstraints = useMemo(
    () => (constraints || []).filter((c) => c.asset_id === assetId),
    [constraints, assetId],
  );
  const scopedGridConfirmation = useMemo(
    () => (gridConfirmation || []).find((l) => l.asset_id === assetId) || null,
    [gridConfirmation, assetId],
  );

  // Binding-constraints pareto — moved here from Technical Performance at
  // the 2026-06-10 METIS review ("the one below there with all the different
  // curtailment binding constraints? That should go into curtailment", at
  // the bottom). /technical resolves a single DUID — the page-level asset.
  const [paretoTech, setParetoTech] = useState(null);
  const [paretoLoading, setParetoLoading] = useState(false);
  useEffect(() => {
    if (!assetId || !period?.start || !period?.end) return undefined;
    let cancelled = false;
    setParetoLoading(true);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/technical`, {
      params: {
        ...periodApiParams(period, { withFrequency: false }),
        asset_id: assetId,
      },
    })
      .then((r) => { if (!cancelled) setParetoTech(r.data); })
      .catch(() => { if (!cancelled) setParetoTech(null); })
      .finally(() => { if (!cancelled) setParetoLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, period]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <TechnicalWorkspaceTabs />
      {/* Header — title left, period selector + refresh right (exactly the
          Technical Performance chrome). */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{
          fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em',
          color: 'text.primary',
        }}>
          Curtailment
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {intermittentAssets.length > 1 && (
            <AssetPicker
              value={assetId || ''}
              onChange={setAssetChoice}
              assets={intermittentAssets}
              colors={colors}
              allowAll={false}
              minWidth={220}
              placeholder="Select asset"
            />
          )}
          <PeriodPicker
            value={period}
            onChange={handlePeriodChange}
            availableFrequencies={CURT_FREQUENCIES}
            size="small"
          />
          <IconButton
            size="small"
            onClick={() => load(period)}
            disabled={loading}
            sx={{ color: 'text.secondary' }}
            title="Refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Sub-header chip — period · range */}
      {period && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, ml: 0.25 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75,
                     color: 'text.secondary', fontSize: '0.8rem' }}>
            <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {period.label}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmtDate(period.start)}
              <Box component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>→</Box>
              {fmtDate(period.end)}
            </Box>
            {assetId && (
              <>
                <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
                <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {intermittentAssets.find((a) => a.asset_id === assetId)?.name || assetId}
                </Box>
              </>
            )}
          </Box>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Body */}
      {loading ? (
        <CurtailmentSkeleton />
      ) : (
        <>
          {!data && !error && (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                border: '1px dashed rgba(255, 255, 255, 0.10)',
                boxShadow: 'none',
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No curtailment data for this period.
              </Typography>
            </Paper>
          )}

          {data && !hasIntermittent && (
            <Alert severity="info">
              This portfolio has no intermittent assets (Solar / Wind). Curtailment
              analysis requires a UIGF forecast, which is only published for
              semi-scheduled renewable generators.
            </Alert>
          )}

          {data && hasIntermittent && (
            <>
              {/* The curtailment waterfall lives on Technical Performance
                  (it replaced the loss-split donut there) — this tab is the
                  per-bucket deep dive. */}
              <ChartSection title="Curtailment Breakdown">
                <CurtailmentGroupedChart
                  curtailment={scopedCurtailment}
                  assets={scopedAssets}
                  frequency={data.frequency}
                />
              </ChartSection>

              <ChartSection title="Curtailment vs Spot Price">
                <CurtailmentVsPriceScatter
                  curtailment={scopedCurtailment}
                  frequency={data.frequency}
                  assets={scopedAssets}
                />
              </ChartSection>

              <ChartSection title="What's driving grid curtailment">
                <GridConstraintsCard
                  constraints={scopedConstraints}
                  gridConfirmation={scopedGridConfirmation}
                  loading={constraintsLoading}
                  assets={scopedAssets}
                />
              </ChartSection>

              {/* Binding constraints pareto — bottom of the curtailment
                  story per the METIS review. Scoped to the header asset. */}
              <Box sx={{ mb: 3 }}>
                <ConstraintParetoCard
                  constraintBinding={paretoTech?.sections?.constraint_binding?.data}
                  assetId={assetId}
                  period={period}
                  loading={paretoLoading}
                  colors={colors}
                />
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default PortfolioCurtailment;
